import { useEffect, useState } from 'react';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { useCroppedSvgLogo } from './useCroppedSvgLogo';

export const RDAP_LOGO_URL_ANNOTATION = 'rdap.reply.com/logo-url';

export interface EntityLogoSourceEntity {
  kind: string;
  metadata: {
    name: string;
    namespace?: string;
    annotations?: Record<string, string>;
  };
}

export const getEntityInitials = (value: string): string => {
  const words = value.replace(/[-_]+/g, ' ').split(/\s+/).filter(Boolean);

  if (words.length === 0) {
    return 'RD';
  }

  return words
    .slice(0, 2)
    .map(word => word[0])
    .join('')
    .toLocaleUpperCase('en-US');
};

export const getEntityLogoUrl = (
  entity: EntityLogoSourceEntity,
): string | undefined => {
  const value = entity.metadata.annotations?.[RDAP_LOGO_URL_ANNOTATION]?.trim();
  return value || undefined;
};

export type EntityLogoSize = 'sm' | 'md' | 'lg';

export interface EntityLogoProps {
  logoUrl?: string;
  entity?: EntityLogoSourceEntity;
  label: string;
  size?: EntityLogoSize;
  className?: string;
}

const isAbsoluteLogoUrl = (value: string): boolean => {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

const getTechDocsAssetPath = (logoUrl: string): string => {
  const parsed = new URL(logoUrl, 'https://rdap.local/');
  const path = parsed.pathname.replace(/^\/+/, '').replace(/^(?:\.\.\/)+/, '');
  const techDocsPath = path.replace(/^(?:docs\/)+/i, '');

  return `${techDocsPath}${parsed.search}${parsed.hash}`;
};

export const resolveEntityLogoUrl = ({
  logoUrl,
  entity,
  techdocsStorageUrl,
  backendBaseUrl,
}: {
  logoUrl?: string;
  entity?: EntityLogoSourceEntity;
  techdocsStorageUrl?: string;
  backendBaseUrl?: string;
}): string | undefined => {
  const value = logoUrl?.trim();

  if (!value) {
    return undefined;
  }

  if (isAbsoluteLogoUrl(value) || value.startsWith('/')) {
    return value;
  }

  if (!entity) {
    return value;
  }

  const namespace = entity.metadata.namespace ?? 'default';
  const kind = entity.kind.toLocaleLowerCase('en-US');
  const name = entity.metadata.name;
  const storageUrl =
    techdocsStorageUrl ??
    (backendBaseUrl ? `${backendBaseUrl}/api/techdocs/static/docs` : undefined);

  if (!storageUrl) {
    return value;
  }

  return `${storageUrl.replace(/\/+$/, '')}/${namespace}/${kind}/${name}/${getTechDocsAssetPath(value)}`;
};

/**
 * Logo mark with an initials fallback, including a runtime fallback when the
 * logo URL is broken (not just absent, via onError). Intended for entity
 * pages; the home page's CatalogHomeCard keeps its own markup but sources
 * the same annotation/initials logic from this file.
 */
export const EntityLogo = ({
  logoUrl,
  entity,
  label,
  size = 'md',
  className,
}: EntityLogoProps) => {
  const configApi = useApi(configApiRef);
  const [hasLogoError, setHasLogoError] = useState(false);
  const resolvedLogoUrl = resolveEntityLogoUrl({
    logoUrl,
    entity,
    techdocsStorageUrl: configApi.getOptionalString('techdocs.storageUrl'),
    backendBaseUrl: configApi.getOptionalString('backend.baseUrl'),
  });
  const shouldShowLogo = Boolean(resolvedLogoUrl) && !hasLogoError;
  const croppedSvgMarkup = useCroppedSvgLogo(
    shouldShowLogo ? resolvedLogoUrl : undefined,
  );
  const classNames = ['rdap-entity-logo', `rdap-entity-logo--${size}`, className]
    .filter(Boolean)
    .join(' ');

  useEffect(() => {
    setHasLogoError(false);
  }, [resolvedLogoUrl]);

  return (
    <span className={classNames} aria-hidden="true">
      {shouldShowLogo ? (
        croppedSvgMarkup ? (
          <span
            className="rdap-entity-logo-svg"
            // Markup is fetched from the logo URL and sanitized (DOMPurify,
            // SVG profile) in useCroppedSvgLogo before reaching here.
            dangerouslySetInnerHTML={{ __html: croppedSvgMarkup }}
          />
        ) : (
          <img
            className="rdap-entity-logo-image"
            src={resolvedLogoUrl}
            alt=""
            onError={() => setHasLogoError(true)}
          />
        )
      ) : (
        getEntityInitials(label)
      )}
    </span>
  );
};
